import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, KeyRound, Lock, ArrowRight, Check, Loader2, ArrowLeft, Sparkles, Eye, EyeOff, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Always returns {ok, status, data}. `data` is the parsed JSON OR a sensible
 * fallback object — never throws on "Unexpected token <" / network errors.
 */
async function safeFetch(url, init = {}) {
  try {
    const res = await fetch(url, init);
    const ct = res.headers.get("content-type") || "";
    let data;
    if (ct.includes("application/json")) {
      data = await res.json().catch(() => ({}));
    } else {
      const text = await res.text().catch(() => "");
      data = { _raw: text.slice(0, 200), message: text ? text.slice(0, 120) : "" };
    }
    return { ok: res.ok, status: res.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: { message: "네트워크 연결을 확인해주세요." }, networkError: true };
  }
}

const STEP = { EMAIL: 1, CODE: 2, PASSWORD: 3, DONE: 4 };

export default function LegacyActivatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const codeRefs = useRef([]);

  // 재전송 카운트다운
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const requestCode = async (isResend = false) => {
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("올바른 이메일을 입력해주세요.");
      return;
    }
    setBusy(true);
    try {
      const { ok, status, data, networkError } = await safeFetch(`${API_URL}/api/auth/legacy/request-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (networkError) { setError(data.message); return; }
      if (status === 429) {
        setError(data.detail || data.message || "잠시 후 다시 시도해주세요.");
        return;
      }
      if (!ok) {
        setError(data.detail || data.message || "요청을 처리하지 못했습니다.");
        return;
      }
      if (!isResend) setStep(STEP.CODE);
      setResendCooldown(60);
    } finally {
      setBusy(false);
    }
  };

  const handleCodeInput = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    const next = [...code];
    next[i] = clean;
    setCode(next);
    if (clean && i < 5) codeRefs.current[i + 1]?.focus();
  };

  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const arr = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
    setCode(arr);
    codeRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const proceedToPassword = () => {
    setError("");
    if (code.some((c) => !c)) {
      setError("6자리 인증번호를 모두 입력해주세요.");
      return;
    }
    setStep(STEP.PASSWORD);
  };

  const activate = async () => {
    setError("");
    if (newPassword.length < 6) {
      setError("새 비밀번호는 6자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setBusy(true);
    try {
      const { ok, data, networkError } = await safeFetch(`${API_URL}/api/auth/legacy/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.join(""),
          new_password: newPassword,
        }),
      });
      if (networkError) { setError(data.message); return; }
      if (!ok) {
        setError(data.detail || data.message || "활성화에 실패했습니다.");
        return;
      }
      setStep(STEP.DONE);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 pt-20 pb-20 px-4">
      <div className="max-w-md mx-auto">
        {/* 상단 헤더 */}
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-6"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          로그인으로 돌아가기
        </Link>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-stone-100">
          {/* 헤더 */}
          <div className="px-6 pt-8 pb-6 text-center bg-gradient-to-br from-burgundy-50 to-rose-50">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3">
              <Sparkles className="w-7 h-7 text-burgundy-600" />
            </div>
            <h1 className="text-xl font-bold text-stone-900">기존 LizTV 회원 활성화</h1>
            <p className="mt-2 text-sm text-stone-600 leading-relaxed">
              LizTV 시절 이메일로 인증하면<br />
              새 비밀번호로 New Coupling을 시작할 수 있어요.
            </p>
          </div>

          {/* 진행도 */}
          <div className="px-6 pt-5">
            <div className="flex items-center justify-between text-[11px] font-medium">
              {[
                { id: STEP.EMAIL, label: "이메일" },
                { id: STEP.CODE, label: "인증번호" },
                { id: STEP.PASSWORD, label: "새 비밀번호" },
              ].map((s, idx, arr) => (
                <div key={s.id} className="flex-1 flex items-center">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${
                      step >= s.id ? "bg-burgundy-600 text-white" : "bg-stone-200 text-stone-400"
                    }`}
                  >
                    {step > s.id ? <Check className="w-4 h-4" /> : s.id}
                  </div>
                  <span className={`ml-1.5 ${step >= s.id ? "text-stone-800" : "text-stone-400"}`}>{s.label}</span>
                  {idx < arr.length - 1 && (
                    <div className={`flex-1 h-px mx-2 ${step > s.id ? "bg-burgundy-400" : "bg-stone-200"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 본문 */}
          <div className="px-6 py-6">
            <AnimatePresence mode="wait">
              {step === STEP.EMAIL && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-4"
                >
                  <label className="block">
                    <span className="text-sm font-semibold text-stone-700 flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />
                      LizTV에서 사용하시던 이메일
                    </span>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      className="mt-2 w-full px-4 py-3.5 text-base rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-400 focus:border-burgundy-300"
                      data-testid="legacy-email-input"
                    />
                  </label>
                  <button
                    onClick={() => requestCode(false)}
                    disabled={busy}
                    className="w-full py-4 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="legacy-send-code-btn"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                    인증번호 받기
                  </button>
                  <p className="text-[11px] text-stone-400 text-center leading-relaxed">
                    보안상 이메일의 존재 여부는 공개하지 않습니다.<br />
                    LizTV 시절 등록된 이메일에만 인증번호가 발송됩니다.
                  </p>
                </motion.div>
              )}

              {step === STEP.CODE && (
                <motion.div
                  key="code"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-4"
                >
                  <div className="text-center">
                    <KeyRound className="w-7 h-7 mx-auto text-burgundy-500 mb-2" />
                    <p className="text-sm text-stone-700">
                      <strong className="text-stone-900">{email}</strong> 로 발송된<br />
                      6자리 인증번호를 입력해주세요
                    </p>
                  </div>
                  <div className="grid grid-cols-6 gap-2" onPaste={handleCodePaste}>
                    {code.map((c, i) => (
                      <input
                        key={i}
                        ref={(el) => (codeRefs.current[i] = el)}
                        type="tel"
                        inputMode="numeric"
                        maxLength={1}
                        value={c}
                        onChange={(e) => handleCodeInput(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !code[i] && i > 0) codeRefs.current[i - 1]?.focus();
                        }}
                        className="w-full h-14 text-center text-2xl font-bold rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-400 focus:border-burgundy-300"
                        data-testid={`legacy-code-${i}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={proceedToPassword}
                    disabled={busy}
                    className="w-full py-4 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-base font-semibold disabled:opacity-50"
                    data-testid="legacy-verify-btn"
                  >
                    다음 단계
                  </button>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => setStep(STEP.EMAIL)}
                      className="text-stone-500 hover:text-stone-700"
                    >
                      이메일 다시 입력
                    </button>
                    <button
                      type="button"
                      disabled={resendCooldown > 0 || busy}
                      onClick={() => requestCode(true)}
                      className="text-burgundy-600 hover:text-burgundy-800 disabled:opacity-50"
                      data-testid="legacy-resend-btn"
                    >
                      {resendCooldown > 0 ? `재전송 ${resendCooldown}초 후` : "인증번호 재전송"}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === STEP.PASSWORD && (
                <motion.div
                  key="pw"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-4"
                >
                  <div className="text-center">
                    <Lock className="w-7 h-7 mx-auto text-burgundy-500 mb-2" />
                    <p className="text-sm text-stone-700">새 비밀번호를 설정해주세요</p>
                    <p className="text-[12px] text-stone-400 mt-1">6자 이상 영문/숫자 권장</p>
                  </div>

                  {/* 새 비밀번호 + 눈동자 토글 */}
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="새 비밀번호 (6자 이상)"
                      className={`w-full pl-4 pr-12 py-3.5 text-base rounded-xl border ${
                        newPassword && newPassword.length < 6
                          ? "border-rose-300 focus:ring-rose-300"
                          : "border-stone-200 focus:ring-burgundy-400"
                      } focus:outline-none focus:ring-2`}
                      data-testid="legacy-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-burgundy-600 transition-colors"
                      aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 보이기"}
                      data-testid="legacy-toggle-pw"
                    >
                      {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* 비밀번호 확인 + 눈동자 토글 */}
                  <div className="relative">
                    <input
                      type={showPw2 ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="새 비밀번호 확인"
                      className={`w-full pl-4 pr-12 py-3.5 text-base rounded-xl border ${
                        confirmPassword && confirmPassword !== newPassword
                          ? "border-rose-400 focus:ring-rose-300"
                          : confirmPassword && confirmPassword === newPassword
                          ? "border-emerald-300 focus:ring-emerald-300"
                          : "border-stone-200 focus:ring-burgundy-400"
                      } focus:outline-none focus:ring-2`}
                      data-testid="legacy-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw2((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-burgundy-600 transition-colors"
                      aria-label={showPw2 ? "비밀번호 숨기기" : "비밀번호 보이기"}
                      data-testid="legacy-toggle-pw2"
                    >
                      {showPw2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* 실시간 안내: 길이 부족 / 일치 상태 */}
                  {newPassword && newPassword.length < 6 && (
                    <p className="text-xs text-rose-500 flex items-center gap-1.5 pl-1" data-testid="legacy-pw-too-short">
                      <AlertCircle className="w-3.5 h-3.5" /> 비밀번호는 6자 이상이어야 합니다.
                    </p>
                  )}
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-rose-500 flex items-center gap-1.5 pl-1" data-testid="legacy-pw-mismatch">
                      <AlertCircle className="w-3.5 h-3.5" /> 두 비밀번호가 일치하지 않습니다.
                    </p>
                  )}
                  {confirmPassword && confirmPassword === newPassword && newPassword.length >= 6 && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1.5 pl-1" data-testid="legacy-pw-match">
                      <Check className="w-3.5 h-3.5" /> 비밀번호가 일치합니다.
                    </p>
                  )}

                  {/* 에러 메시지 (서버 응답 등) — 버튼 위에 배치해 사용자가 즉시 확인 가능 */}
                  {error && (
                    <p className="text-sm text-red-600 text-center bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid="legacy-error-inline">
                      {error}
                    </p>
                  )}

                  <button
                    onClick={activate}
                    disabled={busy || newPassword.length < 6 || newPassword !== confirmPassword}
                    className="w-full py-4 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-base font-semibold flex items-center justify-center gap-2 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                    data-testid="legacy-activate-btn"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                    계정 활성화 완료
                  </button>
                </motion.div>
              )}

              {step === STEP.DONE && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-5 text-center py-4"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-stone-900">계정이 활성화되었어요!</h3>
                    <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                      이제 새 비밀번호로 New Coupling에<br />
                      자유롭게 로그인하실 수 있어요.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-4 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-base font-semibold"
                    data-testid="legacy-done-go-login"
                  >
                    로그인 페이지로 이동
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <p className="mt-4 text-sm text-red-600 text-center bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid="legacy-error">
                {error}
              </p>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-stone-400">
          문제가 있으신가요? <a href="mailto:contact@newcoupling.com" className="underline">contact@newcoupling.com</a>
        </p>
      </div>
    </div>
  );
}
